"use client";

/**
 * VisibilityToggle — owner-only public/private flip for an artifact.
 *
 * Used in three artifact surfaces (niche/plan/brand) to let the owner switch
 * between "Anyone with link can view" and "Only you can view". Visibility is
 * gated by the `private_artifacts` feature (Builder+ tier).
 *
 * Behavior:
 *  - Optimistic flip → POST /api/{session|plan|brand}/[id]/visibility → rollback on error.
 *  - When current state is public AND user lacks Builder+ tier, clicking does
 *    NOT call the API. Instead it surfaces an upgrade prompt (toast w/ "Upgrade"
 *    action linking to /pricing) — same pattern as the workspace tier-block UX.
 *  - Downgrade-safe: if a legacy artifact is already private but tier no longer
 *    grants access, the "make public" direction always works (revert is allowed).
 *  - Caller is responsible for only mounting this when user is the owner AND
 *    user_id !== null (anonymous artifacts can't be made private — backend 400).
 */

import { useState } from "react";
import { useRouter } from "next/navigation";
import { Eye, EyeOff, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { trackEvent } from "@/lib/analytics";

interface VisibilityToggleProps {
  artifactType: "session" | "plan" | "brand";
  artifactId: string;
  initialIsPublic: boolean;
  /** When false (Free / Starter), clicking "make private" opens an upgrade prompt
   *  instead of POSTing. The "make public" direction is always permitted. */
  tierAllowsPrivate: boolean;
}

const ENDPOINT_PREFIX: Record<VisibilityToggleProps["artifactType"], string> = {
  session: "/api/session",
  plan: "/api/plan",
  brand: "/api/brand",
};

export function VisibilityToggle({
  artifactType,
  artifactId,
  initialIsPublic,
  tierAllowsPrivate,
}: VisibilityToggleProps) {
  const router = useRouter();
  const [isPublic, setIsPublic] = useState<boolean>(initialIsPublic);
  const [pending, setPending] = useState<boolean>(false);

  const tooltip = isPublic
    ? "Anyone with link can view"
    : "Only you can view";
  const label = isPublic ? "Public" : "Private";
  const Icon = isPublic ? Eye : EyeOff;

  async function flip() {
    if (pending) return;
    const next = !isPublic;

    // Tier guard — only blocks the public→private direction. Reverting to
    // public is always allowed (downgrade-safe).
    if (next === false && !tierAllowsPrivate) {
      trackEvent("visibility_toggle_blocked", {
        artifactType,
        requiredTier: "builder",
      });
      toast.error("Private artifacts are a Builder feature.", {
        description: "Upgrade to keep your work hidden from search & links.",
        action: {
          label: "Upgrade",
          onClick: () => router.push("/pricing"),
        },
      });
      return;
    }

    // Optimistic flip
    setIsPublic(next);
    setPending(true);

    try {
      const res = await fetch(`${ENDPOINT_PREFIX[artifactType]}/${artifactId}/visibility`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ isPublic: next }),
      });

      if (res.status === 403) {
        // Tier downgraded between mount and click — surface upgrade prompt.
        const data = await res.json().catch(() => ({}));
        setIsPublic(!next); // rollback
        trackEvent("visibility_toggle_blocked", {
          artifactType,
          requiredTier: data?.requiredTier ?? "builder",
        });
        toast.error("Private artifacts are a Builder feature.", {
          action: {
            label: "Upgrade",
            onClick: () => router.push("/pricing"),
          },
        });
        return;
      }

      if (!res.ok) {
        setIsPublic(!next); // rollback
        toast.error("Couldn't update visibility. Try again.");
        return;
      }

      trackEvent("visibility_toggle", { artifactType, isPublic: next });
      toast.success(next ? "Now public — anyone with link can view." : "Now private — only you can view.");
    } catch {
      setIsPublic(!next); // rollback on network error
      toast.error("Network error. Try again.");
    } finally {
      setPending(false);
    }
  }

  return (
    <button
      type="button"
      onClick={flip}
      disabled={pending}
      title={tooltip}
      aria-label={`${label} — ${tooltip}. Click to ${isPublic ? "make private" : "make public"}.`}
      aria-pressed={!isPublic}
      className={
        "inline-flex items-center gap-1.5 rounded-lg border px-3 py-2 text-xs font-medium transition " +
        (isPublic
          ? "border-emerald-500/20 bg-emerald-500/5 text-emerald-300 hover:bg-emerald-500/10"
          : "border-white/10 bg-white/[0.04] text-white/60 hover:bg-white/[0.08]") +
        (pending ? " cursor-wait opacity-70" : " cursor-pointer")
      }
    >
      {pending ? (
        <Loader2 className="size-3.5 animate-spin" aria-hidden="true" />
      ) : (
        <Icon className="size-3.5" aria-hidden="true" />
      )}
      <span>{label}</span>
    </button>
  );
}
